import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Sparkles, ExternalLink, ArrowRight, Building, DollarSign } from "lucide-react";
import { formatSpecial, startingAround } from "../lib/formatSpecial";

const DealCard = ({ deal, index = 0, cta = "apply" }) => {
  const link = deal.source_link;
  const rent = startingAround(deal.base_rent);
  const tag = deal.marketing_tag;
  const special = formatSpecial(deal.special_offer);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
      className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all group"
      data-testid={`deal-card-${index}`}
    >
      {/* Tag strip */}
      {(deal.special_offer || tag) && (
        <div className={`px-4 py-2 ${special ? "bg-[#1F2A44]" : "bg-[#0A2342]"} flex items-start gap-1.5`} data-testid={`deal-special-${index}`}>
          <Sparkles className="w-3.5 h-3.5 text-[#C8A96B] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <span className="block text-white text-xs font-bold tracking-wide truncate">{special ? special.headline : tag}</span>
            {special?.detail && <span className="block text-white/75 text-[11px] truncate">{special.detail}</span>}
          </div>
        </div>
      )}

      <div className="p-5">
        {/* Header */}
        <h3 className="text-lg font-bold text-[#0A2342] mb-1 font-['Playfair_Display'] group-hover:text-[#DE6E4B] transition-colors line-clamp-1">
          {deal.property_name || "Apartment"}
        </h3>
        <div className="flex items-center text-slate-500 text-xs mb-3 gap-1">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{deal.area}{deal.address ? ` — ${deal.address}` : ""}</span>
        </div>

        {/* Details */}
        <div className="flex flex-wrap gap-2 mb-4">
          {rent && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0A2342] text-white text-xs font-semibold" data-testid={`deal-price-${index}`}>
              <DollarSign className="w-3 h-3" /> {rent}
            </span>
          )}
          {deal.bedrooms && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
              <Building className="w-3 h-3" /> {deal.bedrooms} Bed{deal.bathrooms ? ` / ${deal.bathrooms} Bath` : ""}
            </span>
          )}
        </div>

        {/* CTA */}
        {cta === "apply" || !link ? (
          <Link
            to="/apply"
            className="w-full py-2.5 bg-[#DE6E4B] text-white rounded-lg text-sm font-medium hover:bg-[#c5593d] transition-colors flex items-center justify-center gap-2"
            data-testid={`deal-cta-${index}`}
          >
            Get Details <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-2.5 bg-[#DE6E4B] text-white rounded-lg text-sm font-medium hover:bg-[#c5593d] transition-colors flex items-center justify-center gap-2"
            data-testid={`deal-cta-${index}`}
          >
            Check Availability <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default DealCard;
